import api from "../../services/api";
import { Eventbus } from "../../utils";
import { events } from "../../utils/constants";
import logger from "../../utils/logger";
import { formatInTimeZone } from 'date-fns-tz';

const TransactionService = (function () {
    // Private data cache
    let data = [];
    let isLoading = false; // Add loading flag to prevent duplicate requests
    let pendingLoad = null;
    let pendingLoadKey = null;
    let activeLoadToken = null;

    function getData() {
        return [...data]; // Return a copy to prevent external mutation
    }

    function loadData(forceRefresh = false, from, to, maxResults = 0) {
        // Provide default date range if none specified (last 7 days)
        if (!from || !to) {
            const tz = 'Pacific/Auckland';
            const today = new Date();
            const todayNZ = new Date(formatInTimeZone(today, tz, 'yyyy-MM-dd'));
            const sevenDaysAgoNZ = new Date(todayNZ);
            sevenDaysAgoNZ.setDate(todayNZ.getDate() - 6);

            // Format dates in YYYY-MM-DD format using NZ timezone
            to = formatInTimeZone(todayNZ, tz, 'yyyy-MM-dd');
            from = formatInTimeZone(sevenDaysAgoNZ, tz, 'yyyy-MM-dd');

            logger.debug('Using default date range for transactions:', { from, to });
        }

        // Ensure maxResults is a number (sometimes a boolean false may come through from callers)
        const maxResultsParam = (typeof maxResults === 'number' && !Number.isNaN(maxResults)) ? maxResults : (maxResults ? Number(maxResults) : 0);
        const requestKey = JSON.stringify({ from, to, maxResults: maxResultsParam });

        // Reuse identical in-flight requests so callers don't race against an empty cache.
        if (isLoading && pendingLoad && pendingLoadKey === requestKey) {
            return pendingLoad.then((result) => [...result]);
        }

        // If data exists and we're not forcing a refresh, return the cached data
        if (data.length > 0 && !forceRefresh) {
            isLoading = false; // Ensure loading state is reset when returning cached data
            Eventbus.emit(events.TRANSACTION_LOADED, [...data]);
            return Promise.resolve([...data]);
        }

        isLoading = true;
        pendingLoadKey = requestKey;
        const loadToken = {};
        activeLoadToken = loadToken;

        // Otherwise fetch fresh data from the API
        pendingLoad = api
            .getTransactionHistory(from, to, maxResultsParam)
            .then((response) => {
                if (activeLoadToken !== loadToken) {
                    return [...data];
                }
                if (response.code === 'ERR_CANCELED') {
                    return [...data]; // Return existing data on cancellation
                }
                data = response || [];
                logger.debug('Transaction history loaded:', data.length, 'records');
                Eventbus.emit(events.TRANSACTION_LOADED, [...data]);

                return [...data];
            })
            .catch((error) => {
                if (activeLoadToken === loadToken) {
                    logger.error('Error fetching transaction history:', error);
                }
                return [];
            })
            .finally(() => {
                if (activeLoadToken === loadToken) {
                    isLoading = false;
                    pendingLoad = null;
                    pendingLoadKey = null;
                    activeLoadToken = null;
                }
            });

        return pendingLoad;
    }

    function init(from, to, maxResults = 0) {
        return loadData(true, from, to, maxResults);
    }

    function clear() {
        data = [];
        pendingLoad = null;
        pendingLoadKey = null;
        activeLoadToken = null;
        isLoading = false;
    }

    function getLoadingState() {
        return isLoading;
    }

    function searchChildren(searchTerm, maxResults) {
        return api.searchChildren(searchTerm.trim(), maxResults);
    }

    function createMatch(transaction) {
        if (!transaction) {
            return Promise.reject(new Error("Transaction is required"));
        }

        if (!transaction.childId) {
            return Promise.reject(new Error("Child ID is required"));
        }

        logger.debug("Creating match for transaction:", transaction.id, "with child:", transaction.childId);

        return api.updateTransaction(transaction)
            .then((response) => {
                logger.debug("Match created successfully");
                return response;
            })
            .catch((error) => {
                logger.error("Error creating match:", error);
                throw error;
            });
    }

    function getTransactionsByChild(childId) {
        if (!childId) {
            return Promise.reject(new Error("Child ID is required"));
        }
        
        return api.getTransactionsByChild(childId)
            .then((response) => {
                data = response || [];
                logger.debug('Transactions by child loaded:', data.length, 'records');
                Eventbus.emit(events.TRANSACTION_LOADED, [...data]);
                return [...data];
            })
            .catch((error) => {
                logger.error('Error fetching transactions by child:', error);
                return [];
            });
    }

    return {
        getData,
        loadData,
        init,
        clear,
        getLoadingState,
        searchChildren,
        createMatch,
        getTransactionsByChild
    };
})();

export default TransactionService;
